import React, { useState, useEffect } from "react";
import { LOCALSTORAGE } from "../Config";
import DropdownSelect from '../Components/DropdownSelect.jsx';

const findMLBPlayerName = (roster, playerId) => {
  const player = roster.find((player) => player.id === playerId);
  return player ? player.firstLastName : "Unknown Player";
};

// --- Generate MLB / future random options only ---
const generateBettingOptions = (roster, homeTeam, awayTeam, sportType) => {
  const options = {
    gameOutcome: [],
    homeRuns: [],
    RBIs: [],
    strikeouts: [],
  };

  if (homeTeam && awayTeam) {
    options.gameOutcome.push(`${homeTeam} will win`);
    options.gameOutcome.push(`${awayTeam} will win`);
  }

  const selectRandomPlayers = (players, limit) => {
    const shuffled = [...players].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, limit);
  };

  if (sportType === "MLB") {
    const homeRunPlayers = selectRandomPlayers(roster, 5);
    homeRunPlayers.forEach((player) => {
      const name = player.firstLastName || "Unknown Player";
      options.homeRuns.push(`${name} will hit a home run`);
    });

    const rbiPlayers = selectRandomPlayers(roster, 5);
    rbiPlayers.forEach((player) => {
      const name = player.firstLastName || "Unknown Player";
      options.RBIs.push(`${name} will get an RBI`);
    });

    const pitchers = roster.filter((p) => p.primaryPosition?.code === "1");
    const selectedPitchers = selectRandomPlayers(pitchers, 3);
    selectedPitchers.forEach((player) => {
      const name = player.firstLastName || "Unknown Player";
      options.strikeouts.push(`${name} will have over 5 strikeouts`);
    });
  }

  return options;
};

// --- Generate NFL random options from team rosters ---

const generateNflBettingOptions = (homeRoster, awayRoster, homeTeam, awayTeam) => {
  const options = {
    gameOutcome: [],
    passingTDs: [],
    rushingTDs: [],
    receivingTDs: [],
  };

  if (homeTeam && awayTeam) {
    options.gameOutcome.push(`${homeTeam} will win`);
    options.gameOutcome.push(`${awayTeam} will win`);
  }

  const combinedRoster = [...homeRoster, ...awayRoster];

  const selectRandomPlayers = (players, limit) => {
    const shuffled = [...players].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, limit);
  };

  const byPosition = (positionAbbrevs) =>
    combinedRoster.filter((player) =>
      positionAbbrevs.includes(player.position?.abbreviation)
    );

  selectRandomPlayers(byPosition(["QB"]), 4).forEach((player) => {
    options.passingTDs.push(`${player.fullName} will throw a passing touchdown`);
  });

  selectRandomPlayers(byPosition(["RB", "FB"]), 5).forEach((player) => {
    options.rushingTDs.push(`${player.fullName} will score a rushing touchdown`);
  });

  selectRandomPlayers(byPosition(["WR", "TE"]), 5).forEach((player) => {
    options.receivingTDs.push(`${player.fullName} will score a receiving touchdown`);
  });

  return options;
};

// --- Generate NBA random options from team rosters ---
const generateNbaBettingOptions = (homeRoster, awayRoster, homeTeam, awayTeam) => {
  const options = {
    gameOutcome: [],
    points: [],
    rebounds: [],
    assists: [],
  };

  if (homeTeam && awayTeam) {
    options.gameOutcome.push(`${homeTeam} will win`);
    options.gameOutcome.push(`${awayTeam} will win`);
  }

  const combinedRoster = [...homeRoster, ...awayRoster];

  const selectRandomPlayers = (players, limit) => {
    const shuffled = [...players].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, limit);
  };

  const byPosition = (positionAbbrevs) =>
    combinedRoster.filter((player) =>
      positionAbbrevs.includes(player.position?.abbreviation)
    );

  selectRandomPlayers(combinedRoster, 5).forEach((player) => {
    options.points.push(`${player.fullName} will score 20+ points`);
  });

  selectRandomPlayers(byPosition(["PF", "C"]).concat(byPosition(["SF"])), 5).forEach((player) => {
    options.rebounds.push(`${player.fullName} will grab 8+ rebounds`);
  });

  selectRandomPlayers(byPosition(["PG", "SG"]), 5).forEach((player) => {
    options.assists.push(`${player.fullName} will record 5+ assists`);
  });

  return options;
};

const betTypeLabels = {
  gameOutcome: "Game Outcome",
  playerGoals: "Goals",
  playerShots: "Shots on Goal",
  playerAssists: "Assists",
  homeRuns: "Home Runs",
  RBIs: "RBIs",
  strikeouts: "Strikeouts",
  passingTDs: "Passing Touchdowns",
  rushingTDs: "Rushing Touchdowns",
  receivingTDs: "Receiving Touchdowns",
  points: "Points",
  rebounds: "Rebounds",
  assists: "Assists",
};

export const BettingOptions = ({ updateCheckedBets, selectedBets }) => {
  const [sportType, setSportType] = useState("");
  const [roster, setRoster] = useState([]);
  const [teams, setTeams] = useState({ home: "", away: "" });
  const [typedInputs, setTypedInputs] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [selectedActions, setSelectedActions] = useState({});
  const [betOptions, setBetOptions] = useState({});

  const hasSelectedBet = Object.keys(selectedBets).length > 0;
  const selectedBetValue = Object.keys(selectedBets).find(
    (key) => selectedBets[key]
  ) || "";

  const betActions = {
    playerGoals: ["will score first goal", "will score anytime"],
    playerShots: ["will get 2 shots on net", "will get 3+ shots on net"],
    playerAssists: ["will make an assist"],
  };

  // Fetch data on mount
  useEffect(() => {
    const fetchGameData = async () => {
      const storedGame = JSON.parse(localStorage.getItem(LOCALSTORAGE.SELECTEDGAME));
      if (!storedGame) return;

      setSportType(storedGame.sportType);
      const { game_ID, sportType } = storedGame;

      try {
        let rosterData = [];
        let homeTeam = "";
        let awayTeam = "";

        if (sportType === "NHL") {
          const res = await fetch(
            `https://friendly-bets-back-end.vercel.app/api/gamecenter/${game_ID}/play-by-play`
          );
          const data = await res.json();
          rosterData = data.rosterSpots || [];
          homeTeam = data.homeTeam?.abbrev || "";
          awayTeam = data.awayTeam?.abbrev || "";
        } else if (sportType === "MLB") {
          const res = await fetch(
            `https://statsapi.mlb.com/api/v1.1/game/${game_ID}/feed/live`
          );
          const data = await res.json();
          rosterData = Object.values(data.gameData?.players || {});
          homeTeam = data.gameData?.teams?.home?.name || "";
          awayTeam = data.gameData?.teams?.away?.name || "";
        } else if (sportType === "NFL") {
          homeTeam = storedGame.homeTeam || "";
          awayTeam = storedGame.awayTeam || "";

          const fetchRoster = async (teamId) => {
            if (!teamId) return [];
            const res = await fetch(
              `https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${teamId}/roster`
            );
            const data = await res.json();
            // ESPN roster groups athletes by position group (offense/defense/specialTeam)
            return (data.athletes || []).flatMap((group) => group.items || []);
          };

          const [homeRoster, awayRoster] = await Promise.all([
            fetchRoster(storedGame.homeTeamId),
            fetchRoster(storedGame.awayTeamId),
          ]);

          const generated = generateNflBettingOptions(homeRoster, awayRoster, homeTeam, awayTeam);
          setBetOptions(generated);
        } else if (sportType === "NBA") {
          homeTeam = storedGame.homeTeam || "";
          awayTeam = storedGame.awayTeam || "";

          const fetchRoster = async (teamId) => {
            if (!teamId) return [];
            const res = await fetch(
              `https://site.api.espn.com/apis/site/v2/sports/basketball/nba/teams/${teamId}/roster`
            );
            const data = await res.json();
            return data.athletes || [];
          };

          const [homeRoster, awayRoster] = await Promise.all([
            fetchRoster(storedGame.homeTeamId),
            fetchRoster(storedGame.awayTeamId),
          ]);

          const generated = generateNbaBettingOptions(homeRoster, awayRoster, homeTeam, awayTeam);
          setBetOptions(generated);
        }

        setRoster(rosterData);
        setTeams({ home: homeTeam, away: awayTeam });

        if (sportType === "MLB") {
          const generated = generateBettingOptions(rosterData, homeTeam, awayTeam, sportType);
          setBetOptions(generated);
        }
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    fetchGameData();
  }, []);


  // --- NHL Autocomplete handler ---
  const handleInputChange = (betType, value) => {
    setTypedInputs((prev) => ({ ...prev, [betType]: value }));

    if (sportType === "NHL" && value.length > 0) {
      const filtered = roster
        .filter((p) =>
          `${p.firstName?.default || ""} ${p.lastName?.default || ""}`
            .toLowerCase()
            .includes(value.toLowerCase())
        )
        .slice(0, 6)
        .map((p) => `${p.firstName.default} ${p.lastName.default}`);

      setSuggestions((prev) => ({ ...prev, [betType]: filtered }));
    } else {
      setSuggestions((prev) => ({ ...prev, [betType]: [] }));
    }
  };

  const handleSelectSuggestion = (betType, name) => {
    setTypedInputs((prev) => ({ ...prev, [betType]: name }));
    setSuggestions((prev) => ({ ...prev, [betType]: [] }));
  };

  const handleActionSelect = (betType, action) => {
    const name = typedInputs[betType];
    if (!name) return;
    const fullBet = `${name} ${action}`;
    setSelectedActions({ [betType]: action });
    updateCheckedBets(fullBet);
  };

  const handleTeamSelect = (value) => updateCheckedBets(value);

  const handleDropdownSelect = (value) => updateCheckedBets(value);

  const teamOptions = [
    { value: `${teams.home} will win`, label: `${teams.home} will win` },
    { value: `${teams.away} will win`, label: `${teams.away} will win` },
  ].filter((option) => !option.value.startsWith(" will win"));

const handleClearBet = () => {
  setTypedInputs({});
  setSelectedActions({});
  setSuggestions({});
  updateCheckedBets(null);

  // Optional feedback
  alert("Bet cleared! You can make a new one.");
};


  return (
    <div className="mb-3">
      {/* 🏒 NHL Typed Mode */}
      {sportType === "NHL" && (
        <>
          {/* Team Outcome */}
          <div className="mb-4">
            <label className="fw-bold text-info mb-2">{betTypeLabels.gameOutcome}</label>
            <DropdownSelect
              className="w-75 mx-auto"
              options={teamOptions}
              value={
                teamOptions.some((option) => option.value === selectedBetValue)
                  ? selectedBetValue
                  : ""
              }
              placeholder="Select Team"
              onChange={handleTeamSelect}
              disabled={hasSelectedBet}
            />
          </div>

          {/* Player bets with autocomplete */}
          {["playerGoals", "playerShots", "playerAssists"].map((type) => (

            <div key={type} className="mb-4 position-relative">
              <label className="fw-bold text-info mb-2">{betTypeLabels[type]}</label>
              <input
                type="text"
                className="form-control custom-input w-75 mx-auto mb-2"
                placeholder="Type player name"
                value={typedInputs[type] || ""}
                onChange={(e) => handleInputChange(type, e.target.value)}
                disabled={hasSelectedBet}
              />

              {/* Show message if roster is empty and user is typing */}
              {typedInputs[type] && roster.length === 0 && !hasSelectedBet && (
                <div className="alert alert-warning w-75 mx-auto mt-1 p-2" style={{fontSize: '0.95rem'}}>
                  Roster not set yet. Come back closer to game time.
                </div>
              )}

              {suggestions[type]?.length > 0 && !hasSelectedBet && (
                <ul
                  className="list-group position-absolute w-75 mx-auto"
                  style={{
                    zIndex: 20,
                    top: "100%",
                    left: "12.5%",
                    maxHeight: "150px",
                    overflowY: "auto",
                  }}
                >
                  {suggestions[type].map((name, i) => (
                    <li
                      key={i}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleSelectSuggestion(type, name)}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}

              <DropdownSelect
                className="w-75 mx-auto"
                options={betActions[type].map((action) => ({
                  value: action,
                  label: action,
                }))}
                value={selectedActions[type] || ""}
                placeholder="Select Action"
                onChange={(value) => handleActionSelect(type, value)}
                disabled={hasSelectedBet}
              />
            </div>
          ))}

          {/* 🧹 Clear Selection Button */}
          {hasSelectedBet && (
            <button
              className="btn btn-outline-danger w-50 mx-auto mt-3"
              onClick={handleClearBet}
            >
              Clear Selected Bet
            </button>
          )}
        </>
      )}

      {/* ⚾ MLB Dropdown Style */}
      {sportType === "MLB" &&
        ["gameOutcome", "homeRuns", "RBIs", "strikeouts"].map((type) => (
          <div key={type} className="mb-3">
            <label className="fw-bold text-info mb-2">{betTypeLabels[type]}</label>
            <DropdownSelect
              className="w-75 mx-auto"
              options={(betOptions[type] || []).map((option) => ({
                value: option,
                label: option,
              }))}
              value={selectedBetValue}
              placeholder={`Select ${betTypeLabels[type]}`}
              onChange={handleDropdownSelect}
              disabled={hasSelectedBet}
            />
          </div>
        ))}

      {/* 🧹 Clear Button for MLB */}
      {sportType === "MLB" && hasSelectedBet && (
        <button
          className="btn btn-outline-danger w-50 mx-auto mt-3"
          onClick={handleClearBet}
        >
          Clear Selected Bet
        </button>
      )}

      {/* 🏈 NFL Dropdown Style */}
      {sportType === "NFL" &&
        ["gameOutcome", "passingTDs", "rushingTDs", "receivingTDs"].map((type) => (
          <div key={type} className="mb-3">
            <label className="fw-bold text-info mb-2">{betTypeLabels[type]}</label>
            <DropdownSelect
              className="w-75 mx-auto"
              options={(betOptions[type] || []).map((option) => ({
                value: option,
                label: option,
              }))}
              value={selectedBetValue}
              placeholder={`Select ${betTypeLabels[type]}`}
              onChange={handleDropdownSelect}
              disabled={hasSelectedBet}
            />
          </div>
        ))}

      {/* 🧹 Clear Button for NFL */}
      {sportType === "NFL" && hasSelectedBet && (
        <button
          className="btn btn-outline-danger w-50 mx-auto mt-3"
          onClick={handleClearBet}
        >
          Clear Selected Bet
        </button>
      )}

      {/* 🏀 NBA Dropdown Style */}
      {sportType === "NBA" &&
        ["gameOutcome", "points", "rebounds", "assists"].map((type) => (
          <div key={type} className="mb-3">
            <label className="fw-bold text-info mb-2">{betTypeLabels[type]}</label>
            <DropdownSelect
              className="w-75 mx-auto"
              options={(betOptions[type] || []).map((option) => ({
                value: option,
                label: option,
              }))}
              value={selectedBetValue}
              placeholder={`Select ${betTypeLabels[type]}`}
              onChange={handleDropdownSelect}
              disabled={hasSelectedBet}
            />
          </div>
        ))}

      {/* 🧹 Clear Button for NBA */}
      {sportType === "NBA" && hasSelectedBet && (
        <button
          className="btn btn-outline-danger w-50 mx-auto mt-3"
          onClick={handleClearBet}
        >
          Clear Selected Bet
        </button>
      )}
    </div>
  );
};

export { findMLBPlayerName };
